import { makeAutoObservable, observable, runInAction } from "mobx";
import { type AnalyticsSource, track } from "@/lib/analytics";
import { listChanges } from "@/lib/changed-fields";
import { singleTaskProps, trackTaskWrite } from "@/lib/task-analytics";
import { EVENTS_POLISH_FLAG } from "@/lib/launchdarkly/flags";
import type { RootStore } from "./root.store";
import type {
  TIssue,
  TIssueListResponse,
  TIssuePagination,
  TIssueQuery,
  TWorkItemAssigneeDetail,
} from "@/types/issue";
import type { TIssueCollectionScope } from "@/types/view";
import { getIssueCollectionKey } from "@/helpers/view.helper";
import { occurrenceKey } from "./occurrence-key";

interface AllIssuesPageState {
  results: TIssue[];
  hasNext: boolean;
  nextCursor: string | null;
  totalCount: number | null;
}

const EMPTY_ALL_ISSUES_PAGE_STATE: AllIssuesPageState = {
  results: [],
  hasNext: false,
  nextCursor: null,
  totalCount: null,
};

function allIssuesResponseToPageState(response: TIssueListResponse): AllIssuesPageState {
  const results = Array.isArray(response.results) ? response.results : [];
  return {
    results,
    hasNext: response.next_page_results,
    // Null the cursor when there's no next page — the backend may still send
    // a stale cursor string even when next_page_results is false.
    nextCursor: response.next_page_results ? response.next_cursor : null,
    totalCount: response.total_count,
  };
}

type TAsyncState = "idle" | "loading" | "loading-more" | "success" | "error";

function normalizeIssueList(response: TIssueListResponse): {
  issues: TIssue[];
  grouped: Record<string, string[]> | null;
  pagination: TIssuePagination;
} {
  const pagination: TIssuePagination = {
    next_cursor: response.next_cursor,
    prev_cursor: response.prev_cursor,
    next_page_results: response.next_page_results,
    prev_page_results: response.prev_page_results,
    total_count: response.total_count,
    total_count_all_depths: response.total_count_all_depths ?? null,
    count: response.count,
    total_pages: response.total_pages,
    total_results: response.total_results,
  };

  if (Array.isArray(response.results)) {
    return { issues: response.results, grouped: null, pagination };
  }

  const groupedEntries = Object.entries(response.results ?? {}) as Array<
    [string, TIssue[] | { results?: TIssue[]; total_results?: number }]
  >;
  const issues: TIssue[] = [];
  const grouped: Record<string, string[]> = {};

  for (const [group, value] of groupedEntries) {
    const items = Array.isArray(value) ? value : (value.results ?? []);
    grouped[group] = items.map((issue) => occurrenceKey(issue));
    issues.push(...items);
  }

  return { issues, grouped, pagination };
}

export class IssueStore {
  issuesById = observable.map<string, TIssue>();
  collectionIssueIds = observable.map<string, string[]>();
  groupedIssueIdsByCollection = observable.map<string, Record<string, string[]> | null>();
  fetchStateByCollection = observable.map<string, TAsyncState>();
  paginationByCollection = observable.map<string, TIssuePagination | null>();
  queryKeyByCollection = observable.map<string, string>();

  // All-issues (workspace scope) pagination state, keyed by workspace slug.
  allIssuesPageBySlug = observable.map<string, AllIssuesPageState>();
  isLoadingMoreAllIssues = false;

  private inflightAllIssuesLoadMore = new Map<string, Promise<void>>();

  // A stale list response must not clobber a newer one — e.g. the initial
  // unfiltered load landing after a debounced search load silently reverts the
  // list to unfiltered rows (#2638, same class as #1809). Every first-page load
  // takes a fresh token for its slot (per slug for the all-issues page, per
  // collection key for scope fetches) and only the response that is still the
  // latest is applied; appends capture the token at start so a newer first-page
  // load drops the stale append.
  private allIssuesLoadTokenBySlug = new Map<string, number>();
  private loadTokenByCollection = new Map<string, number>();

  private rootStore: RootStore;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    // exception: autoBind intentionally omitted — tests monkey-patch actions
    // such as `createIssue` via vi.spyOn (issue-create-dialog.test.tsx). action.bound installs bound
    // actions as non-configurable. Tracked as a follow-up; refactor tests to
    // mock at the issue service boundary.
    makeAutoObservable(this, {
      rootStore: false,
      inflightAllIssuesLoadMore: false,
      allIssuesLoadTokenBySlug: false,
      loadTokenByCollection: false,
    } as never);
  }

  get projectFetchState(): Record<string, TAsyncState> {
    const acc: Record<string, TAsyncState> = {};
    this.fetchStateByCollection.forEach((value, key) => {
      if (!key.startsWith("project:")) return;
      const [, projectId] = key.split(":");
      if (projectId) acc[projectId] = value;
    });
    return acc;
  }

  getCurrentProjectIssues(projectId: string): TIssue[] {
    return this.getIssuesForScope({ kind: "project", projectId });
  }

  getIssueById(issueId: string): TIssue | undefined {
    // Non-occurrence rows are keyed by their own id, so the direct hit covers
    // them. A virtual occurrence is keyed by (series_id:recurrence_id) but is
    // looked up by its master id (navigation/detail resolve by work-item id) —
    // fall back to the first stored row carrying that work-item id.
    return this.issuesById.get(issueId) ?? this.findIssueByWorkItemId(issueId);
  }

  private findIssueByWorkItemId(workItemId: string): TIssue | undefined {
    for (const issue of this.issuesById.values()) {
      if (issue.id === workItemId) return issue;
    }
    return undefined;
  }

  upsertIssue(issue: TIssue): void {
    this.issuesById.set(occurrenceKey(issue), issue);
  }

  /**
   * Clear a job role from every cached work item that holds it. The backend
   * unassigns the role across all work items when a role is deleted or
   * deactivated (a SET_NULL cascade run explicitly), but the board/list/detail
   * views render from this store's cache — so without this sweep the deleted
   * role's chip lingers on already-loaded items until a refetch. Mutating only
   * the affected items keeps the rest of the cache (and their pagination) intact.
   */
  clearJobRoleFromCache(jobRoleId: string): void {
    runInAction(() => {
      for (const [id, issue] of this.issuesById) {
        if (issue.job_role_id === jobRoleId) {
          this.issuesById.set(id, { ...issue, job_role_id: null, job_role_detail: null });
        }
      }
    });
  }

  /**
   * Reflect a Smart Assign in the cache: the given work items had their job role
   * bulk-assigned to a person, so the backend cleared the role and set the
   * assignee (person beats role). The board/list/detail render from this cache,
   * so without this the assignee column keeps showing the now-stale job role.
   * Mutate only the affected items — clear the role and set the single assignee.
   *
   * Carry the assignee's `detail` onto `assignee_details` too, not just the id:
   * the assignee cell resolves a name from the loaded project-members map OR the
   * item's own `assignee_details`, and the smart-assigned person may not be in
   * the project-members map the current view loaded (they're a workspace member,
   * not necessarily a project member). Without the detail the cell can't resolve
   * the id and renders empty until a refetch (which is when the server sends the
   * detail). Seeding it here makes the assignee show immediately.
   */
  applySmartAssignToCache(
    workItemIds: string[],
    assigneeId: string,
    detail?: TWorkItemAssigneeDetail,
  ): void {
    const ids = new Set(workItemIds);
    runInAction(() => {
      // Patch by the map key (occurrence rows share a work-item id but are keyed
      // by occurrence), so every occurrence row of an assigned series updates too.
      for (const [key, issue] of this.issuesById) {
        if (!ids.has(issue.id)) continue;
        this.issuesById.set(key, {
          ...issue,
          job_role_id: null,
          job_role_detail: null,
          assignee_ids: [assigneeId],
          assignee_details: detail ? [detail] : [],
        });
      }
    });
  }

  getIssuesForScope(scope: TIssueCollectionScope): TIssue[] {
    const key = getIssueCollectionKey(scope);
    return (this.collectionIssueIds.get(key) ?? [])
      .map((id) => this.issuesById.get(id))
      .filter((issue): issue is TIssue => Boolean(issue));
  }

  getGroupedIssues(projectId: string): Record<string, TIssue[]> {
    return this.getGroupedIssuesForScope({ kind: "project", projectId });
  }

  getGroupedIssuesForScope(scope: TIssueCollectionScope): Record<string, TIssue[]> {
    const key = getIssueCollectionKey(scope);
    const grouped = this.groupedIssueIdsByCollection.get(key);
    if (!grouped) return {};

    return Object.fromEntries(
      Object.entries(grouped).map(([group, ids]) => [
        group,
        ids.map((id) => this.issuesById.get(id)).filter((issue): issue is TIssue => Boolean(issue)),
      ]),
    );
  }

  getFetchStateForScope(scope: TIssueCollectionScope): TAsyncState {
    const key = getIssueCollectionKey(scope);
    return this.fetchStateByCollection.get(key) ?? "idle";
  }

  getPaginationForScope(scope: TIssueCollectionScope): TIssuePagination | null {
    const key = getIssueCollectionKey(scope);
    return this.paginationByCollection.get(key) ?? null;
  }

  // ---------------------------------------------------------------------------
  // All-issues workspace scope — DataTable pagination accessor
  // ---------------------------------------------------------------------------

  get allIssuesPagination(): AllIssuesPageState & { isLoadingMore: boolean } {
    const slug = this.rootStore.router.workspaceSlug;
    const page = slug
      ? (this.allIssuesPageBySlug.get(slug) ?? EMPTY_ALL_ISSUES_PAGE_STATE)
      : EMPTY_ALL_ISSUES_PAGE_STATE;
    return { ...page, isLoadingMore: this.isLoadingMoreAllIssues };
  }

  async loadAllIssues(slug: string, query: TIssueQuery = {}): Promise<void> {
    const token = (this.allIssuesLoadTokenBySlug.get(slug) ?? 0) + 1;
    this.allIssuesLoadTokenBySlug.set(slug, token);
    const response = await this.rootStore.workItemService.listWorkspaceWorkItems(slug, query);
    // Superseded by a newer load for this slug — drop the stale page. (#2638)
    if (this.allIssuesLoadTokenBySlug.get(slug) !== token) return;
    const state = allIssuesResponseToPageState(response);
    runInAction(() => {
      // Feed the shared by-id map too: the peek sheet / detail page resolve the
      // issue via getIssueById (issuesById), so list rows must land there — not
      // only in the page-state results.
      for (const issue of state.results) this.issuesById.set(occurrenceKey(issue), issue);
      this.allIssuesPageBySlug.set(slug, state);
    });
  }

  /**
   * Optimistically drop deleted work items from the all-issues page (the slot
   * backing the workspace Tasks/Events lists). Removal is applied locally so a
   * bulk delete reflects immediately and doesn't hinge on a follow-up refetch
   * succeeding — a reload issued around the delete can fail or land out of order
   * under load and leave the deleted rows on screen.
   */
  removeAllIssues(slug: string, ids: string[]): void {
    const idSet = new Set(ids);
    runInAction(() => {
      ids.forEach((id) => this.issuesById.delete(id));
      const page = this.allIssuesPageBySlug.get(slug);
      if (!page) return;
      const results = page.results.filter((issue) => !idSet.has(issue.id));
      const removed = page.results.length - results.length;
      this.allIssuesPageBySlug.set(slug, {
        ...page,
        results,
        totalCount: page.totalCount === null ? null : Math.max(0, page.totalCount - removed),
      });
    });
  }

  async loadMoreAllIssues(slug: string, query: TIssueQuery = {}): Promise<void> {
    const page = this.allIssuesPageBySlug.get(slug) ?? EMPTY_ALL_ISSUES_PAGE_STATE;
    if (!page.hasNext || !page.nextCursor) return;
    const inflight = this.inflightAllIssuesLoadMore.get(slug);
    if (inflight) return inflight;
    // Append is valid only while no newer first-page load supersedes it. (#2638)
    const loadToken = this.allIssuesLoadTokenBySlug.get(slug);
    runInAction(() => {
      this.isLoadingMoreAllIssues = true;
    });
    const promise = (async () => {
      const response = await this.rootStore.workItemService.listWorkspaceWorkItems(slug, {
        ...query,
        cursor: page.nextCursor,
      });
      const next = allIssuesResponseToPageState(response);
      runInAction(() => {
        if (this.allIssuesLoadTokenBySlug.get(slug) !== loadToken) return;
        const existing = this.allIssuesPageBySlug.get(slug) ?? EMPTY_ALL_ISSUES_PAGE_STATE;
        const mergedById = new Map<string, TIssue>(
          existing.results.map((i) => [occurrenceKey(i), i]),
        );
        for (const issue of next.results) mergedById.set(occurrenceKey(issue), issue);
        // Mirror into the shared by-id map (see loadAllIssues) so getIssueById
        // resolves appended rows for the peek sheet / detail page.
        for (const issue of next.results) this.issuesById.set(occurrenceKey(issue), issue);
        this.allIssuesPageBySlug.set(slug, {
          ...next,
          results: [...mergedById.values()],
        });
      });
    })().finally(() => {
      this.inflightAllIssuesLoadMore.delete(slug);
      runInAction(() => {
        this.isLoadingMoreAllIssues = false;
      });
    });
    this.inflightAllIssuesLoadMore.set(slug, promise);
    return promise;
  }

  setScopeIssues(
    scope: TIssueCollectionScope,
    issues: TIssue[],
    grouped: Record<string, string[]> | null,
    pagination?: TIssuePagination | null,
  ): void {
    const key = getIssueCollectionKey(scope);
    issues.forEach((issue) => {
      this.issuesById.set(occurrenceKey(issue), issue);
    });
    this.collectionIssueIds.set(
      key,
      issues.map((issue) => occurrenceKey(issue)),
    );
    this.groupedIssueIdsByCollection.set(key, grouped);
    if (pagination !== undefined) {
      this.paginationByCollection.set(key, pagination);
    }
  }

  appendScopeIssues(
    scope: TIssueCollectionScope,
    issues: TIssue[],
    grouped: Record<string, string[]> | null,
    pagination: TIssuePagination | null,
  ): void {
    const key = getIssueCollectionKey(scope);
    issues.forEach((issue) => {
      this.issuesById.set(occurrenceKey(issue), issue);
    });
    const existingIds = this.collectionIssueIds.get(key) ?? [];
    this.collectionIssueIds.set(
      key,
      uniq([...existingIds, ...issues.map((issue) => occurrenceKey(issue))]),
    );

    if (grouped) {
      const existingGrouped = this.groupedIssueIdsByCollection.get(key) ?? {};
      const nextGrouped = { ...existingGrouped };
      Object.entries(grouped).forEach(([group, ids]) => {
        nextGrouped[group] = uniq([...(existingGrouped[group] ?? []), ...ids]);
      });
      this.groupedIssueIdsByCollection.set(key, nextGrouped);
    }

    this.paginationByCollection.set(key, pagination);
  }

  removeIssueFromScope(scope: TIssueCollectionScope, issueId: string): void {
    const key = getIssueCollectionKey(scope);
    this.collectionIssueIds.set(
      key,
      (this.collectionIssueIds.get(key) ?? []).filter((id) => id !== issueId),
    );
    const grouped = this.groupedIssueIdsByCollection.get(key);
    if (!grouped) return;
    Object.keys(grouped).forEach((group) => {
      grouped[group] = grouped[group].filter((id) => id !== issueId);
    });
  }

  addIssueToScope(scope: TIssueCollectionScope, issue: TIssue): void {
    const key = getIssueCollectionKey(scope);
    const rowKey = occurrenceKey(issue);
    this.issuesById.set(rowKey, issue);
    this.collectionIssueIds.set(key, uniq([rowKey, ...(this.collectionIssueIds.get(key) ?? [])]));
  }

  async fetchIssuesForScope(
    scope: TIssueCollectionScope,
    fetcher: () => Promise<TIssueListResponse>,
    options: { append?: boolean; queryKey?: string; skipIfFresh?: boolean } = {},
  ): Promise<void> {
    const key = getIssueCollectionKey(scope);
    const fetchState = this.fetchStateByCollection.get(key);
    if (
      !options.append &&
      options.skipIfFresh &&
      options.queryKey &&
      this.queryKeyByCollection.get(key) === options.queryKey &&
      (fetchState === "success" || fetchState === "loading")
    ) {
      return;
    }

    // A replace load takes a fresh token; an append rides the current one so a
    // newer replace that starts meanwhile drops the stale append. Only the
    // response that is still the latest may write this collection. (#2638)
    const token = (this.loadTokenByCollection.get(key) ?? 0) + (options.append ? 0 : 1);
    if (!options.append) this.loadTokenByCollection.set(key, token);
    const isLatest = () => (this.loadTokenByCollection.get(key) ?? 0) === token;

    this.fetchStateByCollection.set(key, options.append ? "loading-more" : "loading");
    if (!options.append) {
      this.paginationByCollection.set(key, null);
      if (options.queryKey) this.queryKeyByCollection.set(key, options.queryKey);
    }
    try {
      const response = await fetcher();
      const { issues, grouped, pagination } = normalizeIssueList(response);
      runInAction(() => {
        if (!isLatest()) return; // superseded by a newer load for this scope
        if (options.append) {
          this.appendScopeIssues(scope, issues, grouped, pagination);
        } else {
          this.setScopeIssues(scope, issues, grouped, pagination);
        }
        this.fetchStateByCollection.set(key, "success");
      });
    } catch (error) {
      runInAction(() => {
        if (isLatest()) this.fetchStateByCollection.set(key, "error");
      });
      throw error;
    }
  }

  async fetchProjectIssues(
    workspaceSlug: string,
    projectId: string,
    query: TIssueQuery = {},
    options: { skipIfFresh?: boolean } = {},
  ): Promise<void> {
    // Mount-time callers pass skipIfFresh so concurrently mounted surfaces
    // (page + module picker) and tab returns don't refire an identical query
    // that is already loading or loaded — the same dedup the workspace views
    // use. Post-mutation refreshes omit it to force the refetch.
    await this.fetchIssuesForScope(
      { kind: "project", projectId },
      () => this.rootStore.workItemService.listWorkItems(workspaceSlug, projectId, query),
      { queryKey: JSON.stringify(query), skipIfFresh: options.skipIfFresh },
    );
  }

  async fetchNextProjectIssues(
    workspaceSlug: string,
    projectId: string,
    query: TIssueQuery = {},
  ): Promise<void> {
    const pagination = this.getPaginationForScope({ kind: "project", projectId });
    if (!pagination?.next_cursor) return;
    await this.fetchIssuesForScope(
      { kind: "project", projectId },
      () =>
        this.rootStore.workItemService.listWorkItems(workspaceSlug, projectId, {
          ...query,
          cursor: pagination.next_cursor,
        }),
      { append: true },
    );
  }

  async fetchArchivedIssues(
    workspaceSlug: string,
    projectId: string,
    query: TIssueQuery = {},
  ): Promise<void> {
    await this.fetchIssuesForScope(
      { kind: "archived", projectId },
      () => this.rootStore.issueArchiveService.listArchivedIssues(workspaceSlug, projectId, query),
      { queryKey: JSON.stringify(query) },
    );
  }

  async fetchProjectViewIssues(
    workspaceSlug: string,
    projectId: string,
    viewId: string,
    query: TIssueQuery = {},
    options: { skipIfFresh?: boolean } = {},
  ): Promise<void> {
    await this.fetchIssuesForScope(
      { kind: "project-view", projectId, viewId },
      () => this.rootStore.workItemService.listWorkItems(workspaceSlug, projectId, query),
      { queryKey: JSON.stringify(query), skipIfFresh: options.skipIfFresh },
    );
  }

  async fetchWorkspaceViewIssues(
    workspaceSlug: string,
    viewId: string,
    query: TIssueQuery = {},
  ): Promise<void> {
    const queryKey = JSON.stringify(query);
    await this.fetchIssuesForScope(
      { kind: "workspace-view", viewId },
      () => this.rootStore.workItemService.listWorkspaceWorkItems(workspaceSlug, query),
      { queryKey, skipIfFresh: true },
    );
  }

  async fetchNextWorkspaceViewIssues(
    workspaceSlug: string,
    viewId: string,
    query: TIssueQuery = {},
  ): Promise<void> {
    const pagination = this.getPaginationForScope({ kind: "workspace-view", viewId });
    if (!pagination?.next_cursor) return;
    await this.fetchIssuesForScope(
      { kind: "workspace-view", viewId },
      () =>
        this.rootStore.workItemService.listWorkspaceWorkItems(workspaceSlug, {
          ...query,
          cursor: pagination.next_cursor,
        }),
      { append: true },
    );
  }

  /**
   * Load the COMPLETE filtered workspace work-item set into a `workspace-view`
   * scope collection, paging cursor pages until exhausted — for the non-table
   * layouts (Board / Calendar / Gantt) that group/lay out the whole set rather
   * than a cursor page. The workspace list is cursor-paginated, so we walk pages
   * up to a safety `cap`; when the set is larger than the cap we stop and report
   * `truncated: true` so the surface can show a "showing first N" notice rather
   * than silently dropping rows.
   *
   * Reuses the shared scope plumbing (`getIssuesForScope` reads reactively), so
   * the layouts' optimistic drag/reschedule writes to `issuesById` reflect
   * without a refetch.
   */
  /**
   * The total matching a query, without loading its rows.
   *
   * For the page-header count on a layout whose own fetch is windowed. The
   * Calendar and the Gantt send `start`/`end` so the server can skip rows they
   * cannot draw (NG-1180), which makes their response's `total_count` the
   * window's total rather than the filters'. The header number has to mean the
   * same thing on every layout, so it is asked for separately (NG-1182).
   *
   * `per_page: 1` because only the count is read. Callers strip `expand` as well
   * as the window: the expand path serializes the whole set regardless of page
   * size, so asking it for a count would be the most expensive way to get one.
   */
  async fetchWorkspaceWorkItemTotal(slug: string, query: TIssueQuery): Promise<number | null> {
    const response = await this.rootStore.workItemService.listWorkspaceWorkItems(slug, {
      ...query,
      per_page: 1,
    });
    return response.total_count ?? null;
  }

  async loadFullWorkspaceWorkItemSet(
    workspaceSlug: string,
    viewId: string,
    query: TIssueQuery,
    cap: number,
  ): Promise<{ loadedCount: number; totalCount: number | null; truncated: boolean }> {
    const scope: TIssueCollectionScope = { kind: "workspace-view", viewId };
    const key = getIssueCollectionKey(scope);
    // This writes the same collection slot as fetchIssuesForScope, so it takes
    // a token from the same sequence — whichever replace load starts last wins
    // the slot, and the loser's pages are dropped. (#2638)
    const token = (this.loadTokenByCollection.get(key) ?? 0) + 1;
    this.loadTokenByCollection.set(key, token);
    const isLatest = () => (this.loadTokenByCollection.get(key) ?? 0) === token;
    this.fetchStateByCollection.set(key, "loading");
    try {
      const accumulated: TIssue[] = [];
      let cursor: string | null = null;
      let totalCount: number | null = null;
      let truncated = false;
      // The workspace list page size; one request per page until the BE reports
      // no further page or we reach the cap.
      const perPage = 100;
      // Bound the loop independently of the cap as a hard stop against an
      // unexpected non-terminating cursor.
      const maxPages = Math.ceil(cap / perPage) + 1;
      for (let page = 0; page < maxPages; page += 1) {
        const response: TIssueListResponse =
          await this.rootStore.workItemService.listWorkspaceWorkItems(workspaceSlug, {
            ...query,
            pagination: "cursor",
            per_page: perPage,
            ...(cursor ? { cursor } : {}),
          });
        const results = Array.isArray(response.results) ? response.results : [];
        totalCount = response.total_count ?? totalCount;
        accumulated.push(...results);
        if (accumulated.length >= cap) {
          truncated = response.next_page_results;
          break;
        }
        if (!response.next_page_results || !response.next_cursor) break;
        cursor = response.next_cursor;
      }
      const capped = accumulated.slice(0, cap);
      truncated = truncated || (totalCount !== null && totalCount > capped.length);
      runInAction(() => {
        if (!isLatest()) return; // superseded by a newer load for this scope
        this.setScopeIssues(scope, capped, null, null);
        this.fetchStateByCollection.set(key, "success");
      });
      return { loadedCount: capped.length, totalCount, truncated };
    } catch (error) {
      runInAction(() => {
        if (isLatest()) this.fetchStateByCollection.set(key, "error");
      });
      throw error;
    }
  }

  async createIssue(
    workspaceSlug: string,
    projectId: string,
    data: Partial<TIssue>,
    source: AnalyticsSource = "page",
  ): Promise<TIssue> {
    const issue = await this.rootStore.workItemService.createWorkItem(
      workspaceSlug,
      projectId,
      data,
    );
    runInAction(() => {
      this.issuesById.set(issue.id, issue);
      // Sub-tasks belong only under their parent, not in the root list. Setting
      // a parent via a modal isn't supported yet, so any created issue with a
      // parent_id is an inline sub-task and must not be inserted at root.
      if (!issue.parent_id) {
        const projectKey = getIssueCollectionKey({ kind: "project", projectId });
        const current = this.collectionIssueIds.get(projectKey) ?? [];
        this.collectionIssueIds.set(projectKey, [issue.id, ...current]);
        const grouped = this.groupedIssueIdsByCollection.get(projectKey);
        if (grouped) {
          const groupKey = issue.state_id ?? "ungrouped";
          grouped[groupKey] = [issue.id, ...(grouped[groupKey] ?? [])];
        }
      }
    });
    track("task_created", {
      task_id: issue.id,
      project_id: issue.project_id ?? projectId,
      category: issue.category ?? null,
      is_sub_task: Boolean(issue.parent_id),
      source,
    });
    return issue;
  }

  /**
   * Create a standalone (project-less) event — #3754.
   *
   * Unlike `createIssue` this inserts into no collection: the issue collections
   * are all project/view/module-keyed, and there is no workspace-wide one. The
   * workspace surfaces that can show a standalone event (the Events list) reload
   * from their own data source on create, so caching the row by id is enough.
   */
  async createStandaloneEvent(
    workspaceSlug: string,
    data: Partial<TIssue>,
    source: AnalyticsSource = "page",
  ): Promise<TIssue> {
    const issue = await this.rootStore.workItemService.createWorkspaceWorkItem(workspaceSlug, data);
    runInAction(() => {
      this.issuesById.set(issue.id, issue);
    });
    track("task_created", {
      task_id: issue.id,
      project_id: null,
      category: issue.category ?? null,
      is_sub_task: false,
      source,
    });
    return issue;
  }

  async updateIssue(
    workspaceSlug: string,
    projectId: string,
    issueId: string,
    data: Partial<TIssue>,
    source: AnalyticsSource = "page",
  ): Promise<TIssue> {
    // A project-less work item (a workspace-level event) has no project segment,
    // so the project-scoped route builds `/projects//work-items/<id>/` and 404s
    // — the NG-1082 bug where an event's assignment never sticks. Route it to the
    // workspace-scoped update instead; every updateIssue caller (assignee cell,
    // inline cells, gantt, calendar, board, table columns) gets the fix at once,
    // and this also skips the project-scoped activity.refreshIfCached below, which
    // would 404 the same way.
    if (!projectId) {
      return this.updateWorkspaceIssue(workspaceSlug, issueId, data, source);
    }
    const existing = this.issuesById.get(issueId);
    const backup = existing ? { ...existing } : undefined;
    if (backup) {
      runInAction(() => {
        this.issuesById.set(issueId, { ...backup, ...data });
        this.rootStore.issueDetail.subIssues.patchCachedIssue(issueId, data);
      });
    }
    try {
      const issue = await this.rootStore.workItemService.updateWorkItem(
        workspaceSlug,
        projectId,
        issueId,
        data,
      );
      runInAction(() => {
        const current = this.issuesById.get(issue.id);
        this.issuesById.set(issue.id, { ...current, ...issue });
        this.rootStore.issueDetail.subIssues.patchCachedIssue(issue.id, issue);
      });
      trackTaskWrite(backup, issue, data, source);
      // Property changes land in the activity log too; revalidate an
      // already-open feed so it doesn't go stale next to the editor.
      void this.rootStore.issueDetail.activity.refreshIfCached(workspaceSlug, projectId, issueId);
      return issue;
    } catch (error) {
      runInAction(() => {
        if (backup) {
          this.issuesById.set(issueId, backup);
          this.rootStore.issueDetail.subIssues.patchCachedIssue(issueId, backup);
        }
      });
      throw error;
    }
  }

  async updateWorkspaceIssue(
    workspaceSlug: string,
    issueId: string,
    data: Partial<TIssue>,
    source: AnalyticsSource = "page",
  ): Promise<TIssue> {
    const existing = this.issuesById.get(issueId);
    const backup = existing ? { ...existing } : undefined;
    if (backup) {
      runInAction(() => {
        this.issuesById.set(issueId, { ...backup, ...data });
        this.rootStore.issueDetail.subIssues.patchCachedIssue(issueId, data);
      });
    }
    try {
      const issue = await this.rootStore.workItemService.updateWorkspaceWorkItem(
        workspaceSlug,
        issueId,
        data,
      );
      runInAction(() => {
        const current = this.issuesById.get(issue.id);
        this.issuesById.set(issue.id, { ...current, ...issue });
        this.rootStore.issueDetail.subIssues.patchCachedIssue(issue.id, issue);
      });
      trackTaskWrite(backup, issue, data, source);
      return issue;
    } catch (error) {
      runInAction(() => {
        if (backup) {
          this.issuesById.set(issueId, backup);
          this.rootStore.issueDetail.subIssues.patchCachedIssue(issueId, backup);
        }
      });
      throw error;
    }
  }

  async setPredecessors(
    workspaceSlug: string,
    projectId: string,
    issueId: string,
    sequenceIds: (number | string)[],
    source: AnalyticsSource = "table_inline",
  ): Promise<void> {
    const before = new Set((this.issuesById.get(issueId)?.predecessors ?? []).map((p) => p.id));
    // The endpoint reconciles the relations server-side and returns the
    // authoritative predecessor list, so we apply that rather than guessing
    // optimistically (the row may live under any collection scope).
    const { predecessors } = await this.rootStore.workItemService.setPredecessors(
      workspaceSlug,
      projectId,
      issueId,
      sequenceIds,
    );
    runInAction(() => {
      const current = this.issuesById.get(issueId);
      if (current) this.issuesById.set(issueId, { ...current, predecessors });
      // Sub-task rows render from the sub-issue cache, not issuesById, so patch
      // it too — otherwise editing a sub-task's predecessors wouldn't refresh.
      this.rootStore.issueDetail.subIssues.patchCachedIssue(issueId, { predecessors });
    });
    // The reconcile can add and remove at once, so report each side with its
    // count against the list this row showed before (NG-2056).
    const { added, removed } = listChanges(
      before,
      (predecessors ?? []).map((p) => p.id),
    );
    const props = { task_id: issueId, project_id: projectId, source };
    if (added > 0) track("dependency_created", { ...props, count: added });
    if (removed > 0) track("dependency_deleted", { ...props, count: removed });
  }

  async addIssueSkill(
    workspaceSlug: string,
    // Empty / null for a project-less work item; the service routes on it.
    projectId: string | null | undefined,
    issueId: string,
    skillId: string,
    source: AnalyticsSource = "page",
  ): Promise<void> {
    const existing = this.issuesById.get(issueId);
    if (!existing || existing.skill_ids.includes(skillId)) return;
    const backup = existing.skill_ids;
    runInAction(() => {
      this.issuesById.set(issueId, { ...existing, skill_ids: [...existing.skill_ids, skillId] });
    });
    try {
      await this.rootStore.workspaceSkillService.addIssueSkills(workspaceSlug, projectId, issueId, [
        skillId,
      ]);
      track("task_skill_added", {
        task_id: issueId,
        project_id: projectId || null,
        skill_id: skillId,
        source,
      });
    } catch (err) {
      runInAction(() => {
        const current = this.issuesById.get(issueId);
        if (current) this.issuesById.set(issueId, { ...current, skill_ids: backup });
      });
      throw err;
    }
  }

  async removeIssueSkill(
    workspaceSlug: string,
    // Empty / null for a project-less work item; the service routes on it.
    projectId: string | null | undefined,
    issueId: string,
    skillId: string,
    source: AnalyticsSource = "page",
  ): Promise<void> {
    const existing = this.issuesById.get(issueId);
    if (!existing) return;
    const backup = existing.skill_ids;
    runInAction(() => {
      this.issuesById.set(issueId, {
        ...existing,
        skill_ids: existing.skill_ids.filter((id) => id !== skillId),
      });
    });
    try {
      await this.rootStore.workspaceSkillService.removeIssueSkill(
        workspaceSlug,
        projectId,
        issueId,
        skillId,
      );
      track("task_skill_removed", {
        task_id: issueId,
        project_id: projectId || null,
        skill_id: skillId,
        source,
      });
    } catch (err) {
      runInAction(() => {
        const current = this.issuesById.get(issueId);
        if (current) this.issuesById.set(issueId, { ...current, skill_ids: backup });
      });
      throw err;
    }
  }

  async deleteIssue(
    workspaceSlug: string,
    projectId: string,
    issueId: string,
    source: AnalyticsSource = "page",
  ): Promise<void> {
    const before = this.issuesById.get(issueId);
    await this.rootStore.workItemService.deleteWorkItem(workspaceSlug, projectId, issueId);
    track("task_deleted", singleTaskProps(before, issueId, projectId, source));
    runInAction(() => {
      this.issuesById.delete(issueId);
      const projectKey = getIssueCollectionKey({ kind: "project", projectId });
      this.collectionIssueIds.set(
        projectKey,
        (this.collectionIssueIds.get(projectKey) ?? []).filter((id) => id !== issueId),
      );
      const grouped = this.groupedIssueIdsByCollection.get(projectKey);
      if (grouped) {
        Object.keys(grouped).forEach((group) => {
          grouped[group] = grouped[group].filter((id) => id !== issueId);
        });
      }
    });
  }

  async bulkDeleteIssues(
    workspaceSlug: string,
    projectId: string,
    issueIds: string[],
    source: AnalyticsSource = "page",
  ): Promise<void> {
    await this.rootStore.workItemService.bulkDeleteWorkItems(workspaceSlug, projectId, issueIds);
    track("task_deleted", { project_id: projectId, count: issueIds.length, source });
    const idsToDelete = new Set(issueIds);
    runInAction(() => {
      issueIds.forEach((issueId) => {
        this.issuesById.delete(issueId);
      });
      const projectKey = getIssueCollectionKey({ kind: "project", projectId });
      this.collectionIssueIds.set(
        projectKey,
        (this.collectionIssueIds.get(projectKey) ?? []).filter((id) => !idsToDelete.has(id)),
      );
      const grouped = this.groupedIssueIdsByCollection.get(projectKey);
      if (grouped) {
        Object.keys(grouped).forEach((group) => {
          grouped[group] = grouped[group].filter((id) => !idsToDelete.has(id));
        });
      }
    });
  }

  async updateIssueDates(
    workspaceSlug: string,
    projectId: string,
    updates: Array<{ id: string; start_date?: string | null; target_date?: string | null }>,
    source: AnalyticsSource = "page",
  ): Promise<void> {
    const backup = new Map(
      updates
        .map((update) => {
          const issue = this.issuesById.get(update.id);
          return issue ? [update.id, { ...issue }] : undefined;
        })
        .filter(Boolean) as Array<[string, TIssue]>,
    );

    runInAction(() => {
      updates.forEach((update) => {
        const issue = this.issuesById.get(update.id);
        if (!issue) return;
        this.issuesById.set(update.id, {
          ...issue,
          ...(update.start_date !== undefined ? { start_date: update.start_date } : {}),
          ...(update.target_date !== undefined ? { target_date: update.target_date } : {}),
        });
      });
    });

    try {
      const response = await this.rootStore.workItemService.updateWorkItemDates(
        workspaceSlug,
        projectId,
        updates,
      );
      // The server snaps dates onto working days (M11, NG-36), so what it stored
      // can differ from what was dropped — a bar landing on a weekend or on a
      // covered assignee's holiday moves to the next day they work. Reconcile from
      // the response rather than leaving the optimistic guess on screen, or the
      // Gantt shows a bar the backend has already moved.
      runInAction(() => {
        (response?.resolved ?? []).forEach((resolved) => {
          const issue = this.issuesById.get(resolved.id);
          if (!issue) return;
          this.issuesById.set(resolved.id, {
            ...issue,
            start_date: resolved.start_date,
            target_date: resolved.target_date,
          });
        });
      });
      this.trackDateWrite(projectId, updates, backup, source);
      updates.forEach((update) => {
        void this.rootStore.issueDetail.activity.refreshIfCached(
          workspaceSlug,
          projectId,
          update.id,
        );
      });
    } catch (error) {
      runInAction(() => {
        backup.forEach((issue, id) => {
          this.issuesById.set(id, issue);
        });
      });
      throw error;
    }
  }

  async archiveIssue(
    workspaceSlug: string,
    projectId: string,
    issueId: string,
    source: AnalyticsSource = "page",
  ): Promise<{ archived_at: string }> {
    const existing = this.issuesById.get(issueId);
    const backup = existing ? { ...existing } : undefined;
    const projectKey = getIssueCollectionKey({ kind: "project", projectId });
    const archivedKey = getIssueCollectionKey({ kind: "archived", projectId });
    const prevProjectIds = this.collectionIssueIds.has(projectKey)
      ? [...(this.collectionIssueIds.get(projectKey) ?? [])]
      : undefined;
    const prevArchivedIds = this.collectionIssueIds.has(archivedKey)
      ? [...(this.collectionIssueIds.get(archivedKey) ?? [])]
      : undefined;

    try {
      const response = await this.rootStore.issueArchiveService.archiveIssue(
        workspaceSlug,
        projectId,
        issueId,
      );
      runInAction(() => {
        this.moveIssueToArchived(projectId, issueId, response.archived_at);
      });
      track("task_archived", singleTaskProps(backup, issueId, projectId, source));
      await this.refreshArchiveFamily(workspaceSlug, projectId, [issueId]);
      return response;
    } catch (error) {
      runInAction(() => {
        if (backup) this.issuesById.set(issueId, backup);
        if (prevProjectIds) this.collectionIssueIds.set(projectKey, prevProjectIds);
        if (prevArchivedIds) this.collectionIssueIds.set(archivedKey, prevArchivedIds);
      });
      throw error;
    }
  }

  async restoreIssue(
    workspaceSlug: string,
    projectId: string,
    issueId: string,
    source: AnalyticsSource = "page",
  ): Promise<void> {
    const existing = this.issuesById.get(issueId);
    const backup = existing ? { ...existing } : undefined;
    const projectKey = getIssueCollectionKey({ kind: "project", projectId });
    const archivedKey = getIssueCollectionKey({ kind: "archived", projectId });
    const prevProjectIds = this.collectionIssueIds.has(projectKey)
      ? [...(this.collectionIssueIds.get(projectKey) ?? [])]
      : undefined;
    const prevArchivedIds = this.collectionIssueIds.has(archivedKey)
      ? [...(this.collectionIssueIds.get(archivedKey) ?? [])]
      : undefined;

    try {
      await this.rootStore.issueArchiveService.restoreIssue(workspaceSlug, projectId, issueId);
      runInAction(() => {
        this.moveIssueToActive(projectId, issueId);
      });
      track("task_restored", singleTaskProps(backup, issueId, projectId, source));
      await this.refreshArchiveFamily(workspaceSlug, projectId, [issueId]);
    } catch (error) {
      runInAction(() => {
        if (backup) this.issuesById.set(issueId, backup);
        if (prevProjectIds) this.collectionIssueIds.set(projectKey, prevProjectIds);
        if (prevArchivedIds) this.collectionIssueIds.set(archivedKey, prevArchivedIds);
      });
      throw error;
    }
  }

  async bulkArchiveIssues(
    workspaceSlug: string,
    projectId: string,
    issueIds: string[],
    source: AnalyticsSource = "page",
  ): Promise<{ archived_at: string; succeeded: string[]; failed: string[] }> {
    const projectKey = getIssueCollectionKey({ kind: "project", projectId });
    const archivedKey = getIssueCollectionKey({ kind: "archived", projectId });
    const prevProjectIds = this.collectionIssueIds.has(projectKey)
      ? [...(this.collectionIssueIds.get(projectKey) ?? [])]
      : undefined;
    const prevArchivedIds = this.collectionIssueIds.has(archivedKey)
      ? [...(this.collectionIssueIds.get(archivedKey) ?? [])]
      : undefined;
    const backups = new Map<string, TIssue>();
    issueIds.forEach((id) => {
      const issue = this.issuesById.get(id);
      if (issue) backups.set(id, { ...issue });
    });

    try {
      const response = await this.rootStore.issueArchiveService.bulkArchiveIssues(
        workspaceSlug,
        projectId,
        issueIds,
      );
      runInAction(() => {
        issueIds.forEach((id) => this.moveIssueToArchived(projectId, id, response.archived_at));
      });
      track("task_archived", { project_id: projectId, count: issueIds.length, source });
      await this.refreshArchiveFamily(workspaceSlug, projectId, issueIds);
      return {
        archived_at: response.archived_at,
        succeeded: [...issueIds],
        failed: [],
      };
    } catch (error) {
      runInAction(() => {
        backups.forEach((issue, id) => {
          this.issuesById.set(id, issue);
        });
        if (prevProjectIds) this.collectionIssueIds.set(projectKey, prevProjectIds);
        if (prevArchivedIds) this.collectionIssueIds.set(archivedKey, prevArchivedIds);
      });
      throw error;
    }
  }

  async bulkRestoreIssues(
    workspaceSlug: string,
    projectId: string,
    issueIds: string[],
    source: AnalyticsSource = "page",
  ): Promise<{ succeeded: string[]; failed: string[] }> {
    // The archive API only exposes single-issue restore. Dispatch one
    // request per id and reconcile the local collection per id so a
    // partial backend outcome is mirrored locally instead of silently
    // rolling back successful restores.
    const results = await Promise.allSettled(
      issueIds.map((id) =>
        this.rootStore.issueArchiveService.restoreIssue(workspaceSlug, projectId, id),
      ),
    );

    const succeeded: string[] = [];
    const failed: string[] = [];
    results.forEach((result, index) => {
      if (result.status === "fulfilled") {
        succeeded.push(issueIds[index]);
      } else {
        failed.push(issueIds[index]);
      }
    });

    if (succeeded.length > 0) {
      runInAction(() => {
        succeeded.forEach((id) => this.moveIssueToActive(projectId, id));
      });
      // One event for the action, counting only the restores that landed.
      track("task_restored", { project_id: projectId, count: succeeded.length, source });
      await this.refreshArchiveFamily(workspaceSlug, projectId, succeeded);
    }

    return { succeeded, failed };
  }

  private async refreshArchiveFamily(
    workspaceSlug: string,
    projectId: string,
    rootIds: string[],
  ): Promise<void> {
    if (!this.rootStore.featureFlags.isEnabled(EVENTS_POLISH_FLAG)) return;
    // A cascade may change descendants outside the loaded page. Refetch the
    // server's result rather than guessing which archived children to restore.
    const family = new Set(rootIds);
    let previousSize = -1;
    while (previousSize !== family.size) {
      previousSize = family.size;
      this.issuesById.forEach((item) => {
        if (item.parent_id && family.has(item.parent_id)) family.add(item.id);
      });
    }
    const refreshes: Promise<void>[] = [];
    for (const kind of ["project", "archived"] as const) {
      const key = getIssueCollectionKey({ kind, projectId });
      if (!this.collectionIssueIds.has(key)) continue;
      const query = JSON.parse(this.queryKeyByCollection.get(key) ?? "{}") as TIssueQuery;
      refreshes.push(
        kind === "project"
          ? this.fetchProjectIssues(workspaceSlug, projectId, query)
          : this.fetchArchivedIssues(workspaceSlug, projectId, query),
      );
    }
    const subIssues = this.rootStore.issueDetail.subIssues;
    family.forEach((id) => {
      if (subIssues.hasFetched(id))
        refreshes.push(subIssues.fetchSubIssues(workspaceSlug, projectId, id));
    });
    const results = await Promise.allSettled(refreshes);
    results.forEach((result) => {
      if (result.status === "rejected") {
        // The write already committed; a failed refresh must not roll it back
        // in the local store or report that the archive itself failed.
        console.warn("Archive saved but refreshing the family failed", {
          workspaceSlug,
          projectId,
          rootIds,
          error: result.reason,
        });
      }
    });
  }

  async bulkDeleteArchivedIssues(
    workspaceSlug: string,
    projectId: string,
    issueIds: string[],
    source: AnalyticsSource = "page",
  ): Promise<void> {
    // The archived list deletes one row through this bulk route too; that is a
    // single-task action, so it reports the task rather than a count.
    const single = source !== "bulk" && issueIds.length === 1;
    const before = single ? this.issuesById.get(issueIds[0]) : undefined;
    await this.rootStore.workItemService.bulkDeleteWorkItems(workspaceSlug, projectId, issueIds);
    track(
      "task_deleted",
      single
        ? singleTaskProps(before, issueIds[0], projectId, source)
        : { project_id: projectId, count: issueIds.length, source },
    );
    const idsToDelete = new Set(issueIds);
    runInAction(() => {
      issueIds.forEach((id) => this.issuesById.delete(id));
      const archivedKey = getIssueCollectionKey({ kind: "archived", projectId });
      this.collectionIssueIds.set(
        archivedKey,
        (this.collectionIssueIds.get(archivedKey) ?? []).filter((id) => !idsToDelete.has(id)),
      );
      const grouped = this.groupedIssueIdsByCollection.get(archivedKey);
      if (grouped) {
        Object.keys(grouped).forEach((group) => {
          grouped[group] = grouped[group].filter((id) => !idsToDelete.has(id));
        });
      }
    });
  }

  /**
   * One `task_updated` for a date write. The first update is the task the user
   * moved; any after it are dependents the gantt shifted with it, reported as
   * `count` so the drag still counts once.
   */
  private trackDateWrite(
    projectId: string,
    updates: Array<{ id: string; start_date?: string | null; target_date?: string | null }>,
    before: Map<string, TIssue>,
    source: AnalyticsSource = "page",
  ): void {
    const moved = updates[0];
    if (!moved) return;
    const fields = (["start_date", "target_date"] as const).filter((key) =>
      updates.some((update) => update[key] !== undefined),
    );
    track("task_updated", {
      task_id: moved.id,
      project_id: projectId,
      category: before.get(moved.id)?.category ?? null,
      fields,
      source,
      ...(updates.length > 1 ? { count: updates.length } : {}),
    });
  }

  /** Keep a parent's `sub_issues_count` in step after a child leaves its
   * cached sub-issue list (so the expand chevron disappears at zero). */
  private decrementSubIssuesCount(parentId: string): void {
    const parent = this.issuesById.get(parentId);
    if (!parent) return;
    this.issuesById.set(parentId, {
      ...parent,
      sub_issues_count: Math.max(0, (parent.sub_issues_count ?? 1) - 1),
    });
  }

  private moveIssueToArchived(projectId: string, issueId: string, archivedAt: string): void {
    const projectKey = getIssueCollectionKey({ kind: "project", projectId });
    const archivedKey = getIssueCollectionKey({ kind: "archived", projectId });
    this.collectionIssueIds.set(
      projectKey,
      (this.collectionIssueIds.get(projectKey) ?? []).filter((id) => id !== issueId),
    );
    const projectGrouped = this.groupedIssueIdsByCollection.get(projectKey);
    if (projectGrouped) {
      Object.keys(projectGrouped).forEach((group) => {
        projectGrouped[group] = projectGrouped[group].filter((id) => id !== issueId);
      });
    }
    this.collectionIssueIds.set(
      archivedKey,
      uniq([issueId, ...(this.collectionIssueIds.get(archivedKey) ?? [])]),
    );
    const current = this.issuesById.get(issueId);
    if (current) {
      this.issuesById.set(issueId, { ...current, archived_at: archivedAt });
    }
    // The backend's sub-work-items GET excludes archived children, so an
    // archived child must also leave any cached parent's sub-issue list — else
    // it keeps rendering as a sub-row until a manual reload.
    const affectedParents = this.rootStore.issueDetail.subIssues.removeCachedIssue(issueId);
    affectedParents.forEach((parentId) => this.decrementSubIssuesCount(parentId));
  }

  private moveIssueToActive(projectId: string, issueId: string): void {
    const projectKey = getIssueCollectionKey({ kind: "project", projectId });
    const archivedKey = getIssueCollectionKey({ kind: "archived", projectId });
    this.collectionIssueIds.set(
      archivedKey,
      (this.collectionIssueIds.get(archivedKey) ?? []).filter((id) => id !== issueId),
    );
    const archivedGrouped = this.groupedIssueIdsByCollection.get(archivedKey);
    if (archivedGrouped) {
      Object.keys(archivedGrouped).forEach((group) => {
        archivedGrouped[group] = archivedGrouped[group].filter((id) => id !== issueId);
      });
    }
    this.collectionIssueIds.set(
      projectKey,
      uniq([issueId, ...(this.collectionIssueIds.get(projectKey) ?? [])]),
    );
    const current = this.issuesById.get(issueId);
    if (current) {
      this.issuesById.set(issueId, { ...current, archived_at: null });
    }
    // Mirror of archive: a restored child rejoins its parent's active sub-rows.
    // If the parent's children are cached, refetch them so the row reappears
    // with its full server shape.
    const parentId = current?.parent_id;
    const workspaceSlug = this.rootStore.router.workspaceSlug;
    if (
      parentId &&
      workspaceSlug &&
      this.rootStore.issueDetail.subIssues.subIssuesByIssueId.has(parentId)
    ) {
      void this.rootStore.issueDetail.subIssues.fetchSubIssues(workspaceSlug, projectId, parentId);
    }
  }

  /**
   * Read a work item's FULL record by id or identifier at workspace scope.
   *
   * Always a request, never a cache hit: a row already in `issuesById` may be a
   * list's lean shape, which has no `description_html`, and both callers that
   * render a description editor (the full page, the side panel) would seed it
   * empty and autosave over the saved text (NG-2422). A caller that only needs
   * fields a list row carries checks `getIssueById` first.
   */
  async fetchIssueByIdentifier(workspaceSlug: string, workItem: string): Promise<TIssue> {
    const issue = await this.rootStore.workItemService.getWorkItemByIdentifier(
      workspaceSlug,
      workItem,
    );
    runInAction(() => {
      this.issuesById.set(issue.id, issue);
      if (issue.project_id) {
        const projectKey = getIssueCollectionKey({ kind: "project", projectId: issue.project_id });
        const ids = new Set(this.collectionIssueIds.get(projectKey) ?? []);
        ids.add(issue.id);
        this.collectionIssueIds.set(projectKey, Array.from(ids));
      }
    });
    return issue;
  }
}

function uniq(ids: string[]): string[] {
  return Array.from(new Set(ids));
}
